"use client";

import { c as _c } from "react/compiler-runtime";
import { Combobox as ComboboxPrimitive } from "@base-ui/react/combobox";
import { cn } from "@repo/design-system/utils/cn";
import { ChevronDownIcon, XIcon } from "lucide-react";
import type * as React from "react";
const Combobox = ComboboxPrimitive.Root;
function ComboboxFloatingLabel(t0) {
  const $ = _c(12);
  let children;
  let className;
  let floating;
  let props;
  if ($[0] !== t0) {
    ({
      floating,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = floating;
    $[4] = props;
  } else {
    children = $[1];
    className = $[2];
    floating = $[3];
    props = $[4];
  }
  const t1 = floating ? "-top-1.5 scale-90 bg-brand" : "-top-1.5 translate-y-5 scale-100";
  let t2;
  if ($[5] !== className || $[6] !== t1) {
    t2 = cn("pointer-events-none absolute left-3 z-[1] origin-top-left rounded-lg px-1.5 py-0.5 text-lg leading-3 transition-transform duration-200", t1, className);
    $[5] = className;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== children || $[9] !== props || $[10] !== t2) {
    t3 = <label className={t2} data-slot="combobox-floating-label" {...props}>{children}</label>;
    $[8] = children;
    $[9] = props;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}
function ComboboxInputGroup(t0) {
  const $ = _c(10);
  let className;
  let open;
  let props;
  if ($[0] !== t0) {
    ({
      open,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = open;
    $[3] = props;
  } else {
    className = $[1];
    open = $[2];
    props = $[3];
  }
  const t1 = open && "shadow-[0_0_0_1px_white]";
  let t2;
  if ($[4] !== className || $[5] !== t1) {
    t2 = cn("flex min-h-12 w-full items-center rounded-lg border border-white bg-transparent transition-colors", t1, className);
    $[4] = className;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== props || $[8] !== t2) {
    t3 = <ComboboxPrimitive.InputGroup className={t2} data-slot="combobox-input-group" {...props} />;
    $[7] = props;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
function ComboboxInput(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("h-10 min-w-[60px] flex-1 bg-transparent px-3 text-white outline-none placeholder:text-white", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ComboboxPrimitive.Input className={t1} data-slot="combobox-input" {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function ComboboxClear(t0) {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("shrink-0 rounded-full p-1 hover:bg-white/10", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <XIcon className="size-4 text-white" />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== t1) {
    t3 = <button aria-label="Clear all" className={t1} data-slot="combobox-clear" type="button" {...props}>{t2}</button>;
    $[6] = props;
    $[7] = t1;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function ComboboxIndicator(t0) {
  const $ = _c(8);
  const {
    open,
    className,
    label
  } = t0;
  const t1 = label ?? "Toggle options";
  const t2 = open && "rotate-180";
  let t3;
  if ($[0] !== className || $[1] !== t2) {
    t3 = cn("size-5 shrink-0 opacity-90 transition-transform duration-200", t2, className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t3) {
    t4 = <ChevronDownIcon className={t3} color="currentColor" data-slot="combobox-indicator" />;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t1 || $[6] !== t4) {
    t5 = <ComboboxPrimitive.Trigger aria-label={t1} className="flex shrink-0 items-center">{t4}</ComboboxPrimitive.Trigger>;
    $[5] = t1;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
function ComboboxControls(t0) {
  const $ = _c(13);
  const {
    hasValue,
    onClear,
    open,
    className,
    children,
    label
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("mr-2 flex shrink-0 items-center gap-1", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== hasValue || $[3] !== onClear) {
    t2 = hasValue && onClear && <ComboboxClear onClick={e => {
      e.stopPropagation();
      onClear();
    }} />;
    $[2] = hasValue;
    $[3] = onClear;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== label || $[6] !== open) {
    t3 = <ComboboxIndicator label={label} open={open} />;
    $[5] = label;
    $[6] = open;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== children || $[9] !== t1 || $[10] !== t2 || $[11] !== t3) {
    t4 = <div className={t1} data-slot="combobox-controls">{t2}{children}{t3}</div>;
    $[8] = children;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
function ComboboxContent(t0) {
  const $ = _c(12);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      sideOffset: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const sideOffset = t1 === undefined ? 0 : t1;
  let t2;
  if ($[4] !== className) {
    t2 = cn("bg-background p-0", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== t2) {
    t3 = <ComboboxPrimitive.Popup className={t2} data-slot="combobox-content" {...props} />;
    $[6] = props;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== sideOffset || $[10] !== t3) {
    t4 = <ComboboxPrimitive.Portal><ComboboxPrimitive.Positioner className="pointer-events-auto z-50 w-[var(--anchor-width)] border border-gray-300 data-empty:border-none" sideOffset={sideOffset}>{t3}</ComboboxPrimitive.Positioner></ComboboxPrimitive.Portal>;
    $[9] = sideOffset;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
function ComboboxEmpty(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("hidden px-3 py-2 text-sm opacity-60", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ComboboxPrimitive.Empty className={t1} data-slot="combobox-empty" {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function ComboboxList(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("max-h-72 overflow-y-auto", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ComboboxPrimitive.List className={t1} data-slot="combobox-list" onWheel={_temp} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function _temp(e) {
  return e.stopPropagation();
}
function ComboboxItem(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex cursor-pointer items-center justify-between rounded-none px-3 py-1.5 text-sm", "data-[highlighted]:bg-muted data-[highlighted]:text-black data-[highlighted]:underline", "data-[selected]:bg-secondary data-[selected]:text-white", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ComboboxPrimitive.Item className={t1} data-slot="combobox-item" {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function ComboboxGroup(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ComboboxPrimitive.Group className={t1} data-slot="combobox-group" {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function ComboboxGroupLabel(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("px-2 py-1.5 text-muted-foreground text-xs", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ComboboxPrimitive.GroupLabel className={t1} data-slot="combobox-group-label" {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function ComboboxSeparator(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("-mx-1 my-1 h-px bg-border", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ComboboxPrimitive.Separator className={t1} data-slot="combobox-separator" {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export { Combobox, ComboboxClear, ComboboxContent, ComboboxControls, ComboboxEmpty, ComboboxFloatingLabel, ComboboxGroup, ComboboxGroupLabel, ComboboxIndicator, ComboboxInput, ComboboxInputGroup, ComboboxItem, ComboboxList, ComboboxSeparator };